// LAP — Moteur déterministe de « Mon Conseiller en Organisation ».
//
// Sur la même trame que `buildTimeAdvisor` (ConseillerTemps), cette fonction
// PURE prend en entrée les sources Life de la todo (tâches, projets, listes,
// domaines, habitudes, KPIs, objectifs) et produit un score d'organisation,
// un diagnostic (forces / faiblesses / risques), des actions prioritaires et
// une répartition par domaine — SANS aucun appel IA.
//
// Quatre dimensions (poids égaux) :
//   1. Régularité / Habitudes          (regularite)
//   2. Équilibre par domaine           (equilibre)
//   3. Avancement / Complétion         (avancement)
//   4. Pilotage par objectifs & KPIs   (pilotage)
//
// Chaque dimension porte un flag `available` : une dimension sans donnée
// n'entre PAS dans le score global (poids re-normalisé) et n'est ni force ni
// faiblesse ni risque — elle part dans `notEvaluated`. Même logique que le
// conseiller temps, pour rester cohérent avec l'existant.

const identityT = (key, opts = {}) =>
  opts && Object.prototype.hasOwnProperty.call(opts, 'defaultValue')
    ? opts.defaultValue
    : key

// Palette des barres de répartition par domaine (dégradé sombre cohérent
// avec ConseillerTemps / ConseillerPatrimonial).
const DOMAIN_COLORS = [
  '#4ade80',
  '#60a5fa',
  '#a987ff',
  '#fbbf24',
  '#f87171',
  '#34d399',
  '#f472b6',
  '#22d3ee',
  '#facc15',
  '#fb923c',
]

// ─────────────────────────────────────────────────────────────────────────
// Petits helpers défensifs
// ─────────────────────────────────────────────────────────────────────────

function num(value, fallback = 0) {
  const n = typeof value === 'string' ? parseFloat(value) : value
  return Number.isFinite(n) ? n : fallback
}

function safeArr(value) {
  return Array.isArray(value) ? value : []
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}

function statusFromScore(score) {
  if (score >= 70) return 'good'
  if (score >= 45) return 'warning'
  return 'critical'
}

// Note synthétique A+…F (mêmes seuils que ConseillerPatrimonial).
export function scoreToGrade(score) {
  if (score == null) return '—'
  if (score >= 90) return 'A+'
  if (score >= 80) return 'A'
  if (score >= 70) return 'B'
  if (score >= 55) return 'C'
  if (score >= 40) return 'D'
  if (score >= 25) return 'E'
  return 'F'
}

const TASK_DONE_STATUSES = new Set(['fait', 'terminé', 'termine', 'done'])

function isTaskDone(tk) {
  if (tk.Done === true) return true
  const s = String(tk.Statut || '').toLowerCase()
  return TASK_DONE_STATUSES.has(s)
}

function isTaskOverdue(tk) {
  if (isTaskDone(tk)) return false
  const s = String(tk.Statut || '').toLowerCase()
  if (s.includes('retard')) return true
  // `today` est positionné par le back à true quand DateLimite est dépassée.
  if (tk.today === true) return true
  const due = tk.DateLimite || tk.due_date || tk.deadline
  if (!due) return false
  const d = new Date(due)
  return !Number.isNaN(d.getTime()) && d < new Date()
}

// Résout le nom de domaine d'un item (projet / liste / tâche).
function resolveDomainName(domainObj, lifeDomainId, domainNameById) {
  if (domainObj && (domainObj.domain || domainObj.name)) {
    return domainObj.domain || domainObj.name
  }
  if (lifeDomainId != null && domainNameById[lifeDomainId]) {
    return domainNameById[lifeDomainId]
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────
// Scoreurs par dimension (purs, déterministes)
// ─────────────────────────────────────────────────────────────────────────

function scoreHabits({ habitTasks }) {
  if (!habitTasks.length) return 50
  const rates = habitTasks
    .map((h) => num(h.habit_success_rate, null))
    .filter((v) => v != null)
  let base
  if (rates.length) {
    base = rates.reduce((a, b) => a + b, 0) / rates.length
  } else {
    const doneToday = habitTasks.filter((h) => h.habit_done_today).length
    base = (doneToday / habitTasks.length) * 100
  }
  const avgStreak =
    habitTasks.reduce((a, h) => a + num(h.habit_streak, 0), 0) / habitTasks.length
  const streakBonus = Math.min(avgStreak, 10) * 1.5 // jusqu'à +15
  return clamp(base + streakBonus, 0, 100)
}

function scoreDomainBalance({ distribution }) {
  const counts = Object.values(distribution)
  const total = counts.reduce((a, c) => a + c, 0)
  if (total === 0) return 50
  const k = counts.length
  const coverageBase =
    k <= 1 ? 30 : k === 2 ? 55 : k === 3 ? 70 : k === 4 ? 82 : 90
  const top = Math.max(...counts)
  const topShare = top / total
  // Pénalité de concentration si un domaine capte > 60 % des items.
  const concentrationPenalty = topShare > 0.6 ? (topShare - 0.6) * 100 : 0
  return clamp(coverageBase - concentrationPenalty, 5, 100)
}

function scoreCompletion({ completionRate, overdueTasks, avgProjectProgress, hasProjects }) {
  // Base : taux de complétion des tâches (60 %) + avancement projets (40 %).
  let base
  if (hasProjects) {
    base = completionRate * 0.6 + avgProjectProgress * 0.4
  } else {
    base = completionRate
  }
  const overduePenalty = (overdueTasks || 0) * 5
  return clamp(base - overduePenalty, 5, 100)
}

function scorePilotage({ activeObjectifs, kpiTasks, competenceTasks }) {
  const hasObjectives = activeObjectifs > 0
  const trackedProg = [
    ...kpiTasks.map((k) => num(k.kpi_progress, null)),
    ...competenceTasks.map((c) => num(c.competence_progress, null)),
  ].filter((v) => v != null)

  if (!hasObjectives && !trackedProg.length) {
    return kpiTasks.length || competenceTasks.length ? 45 : 25
  }
  let base = 0
  if (hasObjectives) base += Math.min(activeObjectifs, 5) * 8 // jusqu'à +40
  if (trackedProg.length) {
    const avg = trackedProg.reduce((a, b) => a + b, 0) / trackedProg.length
    base += avg * 0.5 // jusqu'à +50
  } else if (kpiTasks.length || competenceTasks.length) {
    base += 15 // suivi en place mais pas encore de progression
  }
  return clamp(base, 5, 100)
}

// ─────────────────────────────────────────────────────────────────────────
// Point d'entrée public
// ─────────────────────────────────────────────────────────────────────────

export function buildOrganisationAdvisor({
  user = {},
  tasks = [],
  projects = [],
  listes = [],
  objectifs = [],
  domains = [],
  t = identityT,
} = {}) {
  // ── Profil utilisateur ────────────────────────────────────────────────
  const firstName = user.first_name || user.firstName || user.prenom || ''
  const interests = safeArr(user.centres_dinteret || user.interests)
  const concerns = safeArr(user.preoccupations || user.concerns)

  const tasksArr = safeArr(tasks)
  const projectsArr = safeArr(projects)
  const listesArr = safeArr(listes)
  const objectifsArr = safeArr(objectifs)
  const domainsArr = safeArr(domains)

  // Map id de domaine -> nom, pour résoudre le domaine des tâches/projets.
  const domainNameById = {}
  domainsArr.forEach((d) => {
    if (d && d.id != null) domainNameById[d.id] = d.domain || d.name || ''
  })

  // ── Tâches : volumétrie & complétion ─────────────────────────────────
  const totalTasks = tasksArr.length
  const completedTasks = tasksArr.filter(isTaskDone).length
  const pendingTasks = totalTasks - completedTasks
  const overdueTasks = tasksArr.filter(isTaskOverdue).length
  const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  // Tâches typées
  const habitTasks = tasksArr.filter((tk) => tk.TypeTache === 'habitude')
  const kpiTasks = tasksArr.filter((tk) => tk.TypeTache === 'kpi')
  const competenceTasks = tasksArr.filter((tk) => tk.TypeTache === 'competence')

  // ── Projets ──────────────────────────────────────────────────────────
  const activeProjects = projectsArr.filter((p) => {
    const s = String(p.Statut || '').toLowerCase()
    return !p.archived && !p.archive && s !== 'fait' && s !== 'terminé' && s !== 'termine'
  }).length
  const projectsWithProgress = projectsArr
    .map((p) => num(p.Avancement, null))
    .filter((v) => v != null)
  const avgProjectProgress = projectsWithProgress.length
    ? projectsWithProgress.reduce((a, b) => a + b, 0) / projectsWithProgress.length
    : 0

  // ── Objectifs actifs ─────────────────────────────────────────────────
  const activeObjectifs = objectifsArr.filter((o) => {
    const status = String(o.status || o.statut || '').toLowerCase()
    return status !== 'annule' && status !== 'cancelled' && status !== 'fait'
  }).length

  // ── Répartition par domaine (projets + listes + tâches résolvables) ──
  const distribution = {}
  const addToDomain = (name) => {
    if (!name) return
    distribution[name] = (distribution[name] || 0) + 1
  }
  projectsArr.forEach((p) =>
    addToDomain(resolveDomainName(p.domain, p.LifeDomain, domainNameById)),
  )
  listesArr.forEach((l) =>
    addToDomain(resolveDomainName(l.domain, l.LifeDomain, domainNameById)),
  )
  tasksArr.forEach((tk) => {
    const proj = tk.project || {}
    const name = resolveDomainName(
      proj.domain,
      proj.LifeDomain != null ? proj.LifeDomain : tk.LifeDomain,
      domainNameById,
    )
    addToDomain(name)
  })
  const domainsCovered = Object.keys(distribution).length
  const totalDomainItems = Object.values(distribution).reduce((a, c) => a + c, 0)

  // Répartition formatée (barres) triée par volume décroissant.
  const sortedDomains = Object.entries(distribution).sort((a, b) => b[1] - a[1])
  const domainBreakdown = sortedDomains.map(([domain, count], idx) => ({
    domain,
    count,
    percent: totalDomainItems > 0 ? Math.round((count / totalDomainItems) * 100) : 0,
    color: DOMAIN_COLORS[idx % DOMAIN_COLORS.length],
  }))

  // ── Habitudes : agrégats ─────────────────────────────────────────────
  const habitRates = habitTasks
    .map((h) => num(h.habit_success_rate, null))
    .filter((v) => v != null)
  const habitSuccessAvg = habitRates.length
    ? Math.round(habitRates.reduce((a, b) => a + b, 0) / habitRates.length)
    : null

  // ── Dimensions du score global ───────────────────────────────────────
  const dimensions = [
    {
      key: 'regularite',
      label: t('conseillerOrganisation.dimensions.regularite', {
        defaultValue: 'Régularité / Habitudes',
      }),
      score: scoreHabits({ habitTasks }),
      weight: 0.25,
      available: habitTasks.length > 0,
      detail: t('conseillerOrganisation.dimensions.regulariteDetail', {
        defaultValue: 'Constance de vos habitudes : taux de réussite et séries en cours.',
      }),
      dataSource: t('conseillerOrganisation.dimensions.regulariteData', {
        defaultValue: 'Données : vos tâches de type « Habitude » et leur historique de suivi.',
      }),
      emptyHint: t('conseillerOrganisation.dimensions.regulariteEmpty', {
        defaultValue: 'Aucune habitude suivie — créez une routine récurrente pour activer ce score.',
      }),
    },
    {
      key: 'equilibre',
      label: t('conseillerOrganisation.dimensions.equilibre', {
        defaultValue: 'Équilibre par domaine',
      }),
      score: scoreDomainBalance({ distribution }),
      weight: 0.25,
      available: totalDomainItems >= 2,
      detail: t('conseillerOrganisation.dimensions.equilibreDetail', {
        defaultValue: 'Répartition de vos tâches, projets et listes entre les domaines de vie.',
      }),
      dataSource: t('conseillerOrganisation.dimensions.equilibreData', {
        defaultValue: 'Données : domaines de vos projets, listes et tâches rattachées.',
      }),
      emptyHint: t('conseillerOrganisation.dimensions.equilibreEmpty', {
        defaultValue: 'Pas assez d’éléments rattachés à des domaines — classez vos projets et listes par domaine.',
      }),
    },
    {
      key: 'avancement',
      label: t('conseillerOrganisation.dimensions.avancement', {
        defaultValue: 'Avancement / Complétion',
      }),
      score: scoreCompletion({
        completionRate,
        overdueTasks,
        avgProjectProgress,
        hasProjects: projectsWithProgress.length > 0,
      }),
      weight: 0.25,
      available: totalTasks > 0 || projectsArr.length > 0,
      detail: t('conseillerOrganisation.dimensions.avancementDetail', {
        defaultValue: 'Taux de tâches terminées, avancement des projets et tâches en retard.',
      }),
      dataSource: t('conseillerOrganisation.dimensions.avancementData', {
        defaultValue: 'Données : statut de vos tâches, retard éventuel et avancement de vos projets.',
      }),
      emptyHint: t('conseillerOrganisation.dimensions.avancementEmpty', {
        defaultValue: 'Aucune tâche ni projet — ajoutez-en pour activer ce score.',
      }),
    },
    {
      key: 'pilotage',
      label: t('conseillerOrganisation.dimensions.pilotage', {
        defaultValue: 'Pilotage par objectifs & KPIs',
      }),
      score: scorePilotage({ activeObjectifs, kpiTasks, competenceTasks }),
      weight: 0.25,
      available:
        objectifsArr.length > 0 || kpiTasks.length > 0 || competenceTasks.length > 0,
      detail: t('conseillerOrganisation.dimensions.pilotageDetail', {
        defaultValue: 'Pilotage par objectifs mesurables, KPIs suivis et compétences en progression.',
      }),
      dataSource: t('conseillerOrganisation.dimensions.pilotageData', {
        defaultValue: 'Données : vos objectifs actifs, vos tâches KPI et compétences avec progression.',
      }),
      emptyHint: t('conseillerOrganisation.dimensions.pilotageEmpty', {
        defaultValue: 'Aucun objectif ni KPI — définissez des cibles mesurables pour activer ce score.',
      }),
    },
  ].map((d) => ({
    ...d,
    score: Math.round(d.score),
    status: d.available ? statusFromScore(d.score) : 'unknown',
  }))

  // Score global sur les seules dimensions évaluées (poids re-normalisé).
  const evaluatedDims = dimensions.filter((d) => d.available)
  const evaluatedWeight = evaluatedDims.reduce((acc, d) => acc + d.weight, 0)
  const globalScore =
    evaluatedWeight > 0
      ? Math.round(
          evaluatedDims.reduce((acc, d) => acc + d.score * d.weight, 0) /
            evaluatedWeight,
        )
      : null

  // ── Diagnostic ───────────────────────────────────────────────────────
  const strengths = dimensions
    .filter((d) => d.available && d.status === 'good')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const weaknesses = dimensions
    .filter((d) => d.available && d.status === 'warning')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const risks = dimensions
    .filter((d) => d.available && d.status === 'critical')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const notEvaluated = dimensions
    .filter((d) => !d.available)
    .map((d) => ({ key: d.key, title: d.label, detail: d.emptyHint }))

  // ── Actions prioritaires ─────────────────────────────────────────────
  const actionsPrioritaires = []

  if (overdueTasks > 0) {
    actionsPrioritaires.push({
      id: 'overdue-tasks',
      title: t('conseillerOrganisation.actions.overdueTitle', {
        defaultValue: 'Solder vos tâches en retard',
        count: overdueTasks,
      }),
      description: t('conseillerOrganisation.actions.overdueDesc', {
        defaultValue: `${overdueTasks} tâche(s) en retard à reprogrammer, déléguer ou abandonner cette semaine.`,
        count: overdueTasks,
      }),
      impact: 'high',
      effortMinutes: 30,
      route: '/todos',
    })
  }
  if (habitTasks.length === 0) {
    actionsPrioritaires.push({
      id: 'no-habits',
      title: t('conseillerOrganisation.actions.noHabitsTitle', {
        defaultValue: 'Installer une première routine',
      }),
      description: t('conseillerOrganisation.actions.noHabitsDesc', {
        defaultValue:
          'Créez une tâche récurrente de type « Habitude ». La régularité est le socle d’une bonne organisation.',
      }),
      impact: 'high',
      effortMinutes: 15,
      route: '/todos',
    })
  }
  if (activeObjectifs === 0 && kpiTasks.length === 0) {
    actionsPrioritaires.push({
      id: 'no-objectives',
      title: t('conseillerOrganisation.actions.noObjectivesTitle', {
        defaultValue: 'Définir des objectifs mesurables',
      }),
      description: t('conseillerOrganisation.actions.noObjectivesDesc', {
        defaultValue:
          'Sans cibles chiffrées, impossible de piloter. Posez 1 à 3 objectifs ou KPIs à suivre.',
      }),
      impact: 'high',
      effortMinutes: 30,
      route: '/mesobjectifs',
    })
  }
  if (totalDomainItems >= 2 && domainsCovered <= 2) {
    actionsPrioritaires.push({
      id: 'unbalanced-domains',
      title: t('conseillerOrganisation.actions.balanceTitle', {
        defaultValue: 'Diversifier vos domaines',
      }),
      description: t('conseillerOrganisation.actions.balanceDesc', {
        defaultValue:
          'Votre énergie se concentre sur trop peu de domaines de vie. Ouvrez un projet ou une liste sur un domaine négligé.',
      }),
      impact: 'medium',
      effortMinutes: 20,
      route: '/todos',
    })
  }
  if (totalTasks > 0 && completionRate < 40) {
    actionsPrioritaires.push({
      id: 'low-completion',
      title: t('conseillerOrganisation.actions.completionTitle', {
        defaultValue: 'Clôturer les tâches faciles',
      }),
      description: t('conseillerOrganisation.actions.completionDesc', {
        defaultValue:
          'Votre taux de complétion est bas. Terminez 3 petites tâches dès aujourd’hui pour relancer la dynamique.',
      }),
      impact: 'medium',
      effortMinutes: 25,
      route: '/todos',
    })
  }
  if (projectsArr.length === 0) {
    actionsPrioritaires.push({
      id: 'no-projects',
      title: t('conseillerOrganisation.actions.noProjectsTitle', {
        defaultValue: 'Structurer en projets',
      }),
      description: t('conseillerOrganisation.actions.noProjectsDesc', {
        defaultValue:
          'Regroupez vos tâches dans des projets rattachés à un domaine pour gagner en lisibilité.',
      }),
      impact: 'medium',
      effortMinutes: 20,
      route: '/todos',
    })
  }

  // ── Recommandations narratives ───────────────────────────────────────
  const recommendations = []
  if (habitSuccessAvg != null && habitSuccessAvg < 60 && habitTasks.length > 0) {
    recommendations.push(
      t('conseillerOrganisation.reco.habits', {
        defaultValue: `Vos habitudes tournent à ${habitSuccessAvg}% de réussite : réduisez leur nombre et ancrez-les à un déclencheur quotidien fixe.`,
        rate: habitSuccessAvg,
      }),
    )
  }
  if (domainsCovered > 0 && domainBreakdown[0] && domainBreakdown[0].percent >= 60) {
    recommendations.push(
      t('conseillerOrganisation.reco.concentration', {
        defaultValue: `Le domaine « ${domainBreakdown[0].domain} » concentre ${domainBreakdown[0].percent}% de votre activité. Veillez à ne pas négliger les autres pans de votre vie.`,
        domain: domainBreakdown[0].domain,
        percent: domainBreakdown[0].percent,
      }),
    )
  }
  if (interests.length) {
    recommendations.push(
      t('conseillerOrganisation.reco.interests', {
        defaultValue: `Sur vos centres d’intérêt (${interests.slice(0, 3).join(', ')}), créez une liste dédiée pour transformer l’envie en actions concrètes.`,
        list: interests.slice(0, 3).join(', '),
      }),
    )
  }

  // ── Prompts pour le chat ─────────────────────────────────────────────
  const suggestedPrompts = [
    t('conseillerOrganisation.prompts.prioritize', {
      defaultValue: 'Par quoi devrais-je commencer cette semaine ?',
    }),
    t('conseillerOrganisation.prompts.balance', {
      defaultValue: 'Quels domaines de vie est-ce que je néglige ?',
    }),
    t('conseillerOrganisation.prompts.habits', {
      defaultValue: 'Aide-moi à construire une routine durable.',
    }),
    t('conseillerOrganisation.prompts.declutter', {
      defaultValue: 'Quelles tâches puis-je abandonner ?',
    }),
  ]

  return {
    userProfile: {
      firstName,
      interests,
      concerns,
    },
    metrics: {
      totalTasks,
      completedTasks,
      pendingTasks,
      overdueTasks,
      completionRate: Math.round(completionRate),
      projetsCount: projectsArr.length,
      activeProjects,
      avgProjectProgress: Math.round(avgProjectProgress),
      listsCount: listesArr.length,
      domainsCovered,
      habitCount: habitTasks.length,
      habitSuccessAvg,
      kpiCount: kpiTasks.length,
      competenceCount: competenceTasks.length,
      objectivesCount: activeObjectifs,
    },
    orgScore: {
      global: globalScore,
      grade: scoreToGrade(globalScore),
      dimensions,
      evaluatedCount: evaluatedDims.length,
      totalCount: dimensions.length,
    },
    diagnostic: {
      strengths,
      weaknesses,
      risks,
      notEvaluated,
    },
    domainBreakdown,
    actionsPrioritaires,
    recommendations,
    suggestedPrompts,
  }
}

export default buildOrganisationAdvisor
